import { Component, computed, effect, inject, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatCardModule } from '@angular/material/card';
import { MatFormField, MatLabel, MatOption, MatSelect } from '@angular/material/select';
import { MatDrawer, MatDrawerContainer } from '@angular/material/sidenav';
import { ActivatedRoute, Router, RouterLinkWithHref } from '@angular/router';
import { SideNavModule } from '@eagle-i/vulcan-component-library';
import { Store } from '@ngrx/store';
import { take } from 'rxjs';
import { Territory } from '../../../../core/classes/territory';
import { AppService } from '../../../../services/app.service';
import { LocationService } from '../../../../services/location.service';
import { LoadingMaskComponent } from '../../../../shared/components/loading-mask/loading-mask.component';
import { selectTerritories } from '../../../../state/location/location.reducer';
import { AppendixPageComponent } from './report-pages/appendix-page/appendix-page.component';
import { CostOutagePageComponent } from './report-pages/cost-outage-page/cost-outage-page.component';
import { OutageDataPageComponent } from './report-pages/outage-data-page/outage-data-page.component';
import { RestorationMetricPageComponent } from './report-pages/restoration-metric-page/restoration-metric-page.component';
import {
  StatewideReliabilityPageComponent,
} from './report-pages/statewide-reliability-page/statewide-reliability-page.component';
import { SystemResiliencePageComponent } from './report-pages/system-resillence-page/system-resilience-page.component';

@Component({
  selector: 'tgrid-state-report-helper',
  templateUrl: './state-report-helper.component.html',
  styleUrls: ['./state-report-helper.component.scss'],
  imports: [MatDrawerContainer, MatDrawer, MatSelect, MatFormField, MatLabel, MatOption, MatCardModule,
    AppendixPageComponent,
    CostOutagePageComponent,
    OutageDataPageComponent,
    RestorationMetricPageComponent,
    StatewideReliabilityPageComponent,
    SystemResiliencePageComponent,
    SideNavModule, RouterLinkWithHref, LoadingMaskComponent],
})
export class StateReportHelperComponent {
  private appService = inject(AppService);
  protected locationService = inject(LocationService);
  private store = inject(Store);
  protected activatedRoute = inject(ActivatedRoute);
  protected router = inject(Router);


  protected selectedAbbreviation: WritableSignal<string> = signal('');

  protected stateOptions: WritableSignal<Territory[]> = signal([]);
  protected yearOptions: WritableSignal<number[]> = signal([2025, 2024, 2023, 2022, 2021, 2020, 2019, 2018, 2017, 2016, 2015]);

  protected selectedState = computed(() => {
    const stateAbbr = this.selectedAbbreviation();
    return this.stateOptions().find(s => s.abbreviation === stateAbbr);
  });


  protected pageRoutes = computed(() => {
    const stateAbbr = this.selectedAbbreviation();
    const year = this.selectedYear();
    const page = this.selectedPage();
    return [
      {
        id: 'cost-outage',
        active: page === 'cost-outage',
        display: 'Cost Outage',
        route: `/app/reports/state-helper/${stateAbbr}/${year}/cost-outage`,
      },
      {
        id: 'outage-data',
        active: page === 'outage-data',
        display: 'Outage Data',
        route: `/app/reports/state-helper/${stateAbbr}/${year}/outage-data`,
      },
      {
        id: 'restoration',
        active: page === 'restoration',
        display: 'Restoration',
        route: `/app/reports/state-helper/${stateAbbr}/${year}/restoration`,
      },
      {
        id: 'reliability',
        active: page === 'reliability',
        display: 'Reliability',
        route: `/app/reports/state-helper/${stateAbbr}/${year}/reliability`,
      },
      {
        id: 'resilience',
        active: page === 'resilience',
        display: 'Resilience',
        route: `/app/reports/state-helper/${stateAbbr}/${year}/resilience`,
      },
      // {
      //   id: 'appendix',
      //   active: page === 'appendix',
      //   display: 'Appendix',
      //   route: `/app/reports/state-helper/${stateAbbr}/${year}/appendix`
      // }
    ];
  });

  // Defaults

  protected selectedPage = signal<string>('cost-outage');
  protected selectedYear = signal<number>(this.yearOptions()[0]);


  constructor() {
    effect(() => {
      const stateAbbr = this.selectedAbbreviation();
      const year = this.selectedYear();
      const page = this.selectedPage();
      this.navigate({ stateAbbr, year, page });
    });

    this.activatedRoute.params.pipe(takeUntilDestroyed()).subscribe(params => {
      const stateAbbr = params['stateAbbr'] ?? this.appService.selectedStates().map(s => s.abbreviation).join(',') ?? '';
      const year = params['year'] ?? this.selectedYear() ?? this.yearOptions()[0];
      const page = params['page'] ?? this.selectedPage() ?? 'cost-outage';

      this.selectedAbbreviation.set(stateAbbr);
      this.selectedYear.set(+year);
      this.selectedPage.set(page);
      //update the url if needed to change. do this after we update the signals so we can avoid multiple navigations
      this.navigate({ stateAbbr, year, page });
    });

    this.store.select(selectTerritories).pipe(take(1)).subscribe(states => {
      this.stateOptions.set(states);
    });
  }


  protected navigate(obj: Partial<{ stateAbbr: string, year: number, page: string; }>) {
    let { stateAbbr, year, page } = obj;
    stateAbbr = stateAbbr || this.selectedAbbreviation();
    year = year || this.selectedYear();
    page = page || this.selectedPage();
    this.router.navigate(['/app/reports/state-helper', stateAbbr, year, page], {
      queryParamsHandling: 'merge',
    });
  }
}
